import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    // Get initial session with timeout protection
    const getSession = async () => {
      try {
        console.log('🔄 AuthContext: Getting initial session...');
        
        // Set a timeout for session retrieval
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session timeout')), 5000)
        );
        
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]);

        if (!mounted) return;

        if (error) {
          console.error('❌ Error getting session:', error);
          // Don't throw error, just continue without session
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          console.log('✅ Initial session loaded:', session?.user?.email || 'No user');
        }
      } catch (error) {
        console.error('❌ Session initialization error:', error);
        // Continue without session on timeout/error
        if (mounted) {
          setSession(null);
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setAuthInitialized(true);
        }
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      console.log('🔄 Auth state changed:', event, session?.user?.email || 'No user');
      
      // Add small delay to prevent race conditions
      setTimeout(() => {
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Initialize user settings on sign up or sign in
        if (event === 'SIGNED_IN' && session?.user) {
          ensureUserSettings(session.user.id);
        }
        
        // Clear local data on sign out
        if (event === 'SIGNED_OUT') {
          console.log('🧹 Clearing local data on sign out');
          // Clear all local storage data related to the app
          try {
            localStorage.removeItem('codeEditor_projects');
            localStorage.removeItem('codeEditor_settings');
          } catch (error) {
            console.error('Error clearing localStorage:', error);
          }
        }
      }, 100);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const ensureUserSettings = async (userId) => {
    try {
      console.log('⚙️ Ensuring user settings for:', userId);
      
      // Check if user settings exist with timeout
      const settingsPromise = supabase
        .from('user_settings_ac2025')
        .select('*')
        .eq('user_id', userId)
        .single();
        
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Settings check timeout')), 3000)
      );
      
      const { data: existingSettings, error: fetchError } = await Promise.race([
        settingsPromise,
        timeoutPromise
      ]);

      if (fetchError && fetchError.code === 'PGRST116') {
        // No settings found, create default settings
        const defaultSettings = {
          aiProvider: 'openai',
          aiApiKey: '',
          customApiUrl: '',
          localModelId: null,
          theme: 'dark',
          fontSize: 14,
          autoSave: true
        };

        const { error: insertError } = await supabase
          .from('user_settings_ac2025')
          .insert({
            user_id: userId,
            settings: defaultSettings
          });

        if (insertError) {
          console.error('❌ Error creating user settings:', insertError);
        } else {
          console.log('✅ User settings initialized');
        }
      } else if (fetchError && fetchError.message !== 'Settings check timeout') {
        console.error('❌ Error checking user settings:', fetchError);
      } else if (!fetchError) {
        console.log('✅ User settings already exist');
      }
    } catch (error) {
      console.error('❌ Error ensuring user settings:', error);
    }
  };

  const signUp = async (email, password) => {
    try {
      setLoading(true);
      console.log('🔄 Starting sign up for:', email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          // Auto-confirm for easier testing - remove in production
          data: {
            email_confirm: true
          }
        }
      });

      if (error) throw error;

      console.log('✅ Sign up successful:', email);
      return { data, error: null };
    } catch (error) {
      console.error('❌ Sign up error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      console.log('🔄 Starting sign in for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      console.log('✅ Sign in successful:', email);
      return { data, error: null };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      console.log('🔄 Starting sign out...');
      
      // Set a timeout for sign out
      const signOutPromise = supabase.auth.signOut();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sign out timeout')), 5000)
      );
      
      const { error } = await Promise.race([signOutPromise, timeoutPromise]);
      
      if (error) throw error;

      // Force clear state immediately
      setUser(null);
      setSession(null);
      
      // Clear local storage
      try {
        localStorage.removeItem('codeEditor_projects');
        localStorage.removeItem('codeEditor_settings');
      } catch (storageError) {
        console.error('Error clearing localStorage:', storageError);
      }

      console.log('✅ Sign out successful');
      return { error: null };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      
      // Force clear state even on error to prevent stuck loading
      setUser(null);
      setSession(null);
      setLoading(false);
      
      // Try to clear localStorage anyway
      try {
        localStorage.removeItem('codeEditor_projects');
        localStorage.removeItem('codeEditor_settings');
      } catch (storageError) {
        console.error('Error clearing localStorage on failed signout:', storageError);
      }
      
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('❌ Reset password error:', error);
      return { error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('❌ Update password error:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    authInitialized,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};